<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ColorPicker from 'dashboard/components-next/colorpicker/ColorPicker.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import InlineStorageImage from 'shared/components/InlineStorageImage.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  widgetColor: {
    type: String,
    default: '#1f93ff',
  },
  widgetLogoUrl: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'update:modelValue',
  'update:widgetColor',
  'upload-widget-logo',
  'delete-widget-logo',
]);

const onWidgetLogoChange = event => {
  const file = event.target.files?.[0];
  if (!file) return;

  emit('upload-widget-logo', { file, url: URL.createObjectURL(file) });
  event.target.value = '';
};

const { t } = useI18n();

const appearance = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

const updateField = (key, value) => {
  appearance.value = { ...appearance.value, [key]: value };
};

const headerStyleSelectOptions = computed(() => [
  {
    value: 'solid',
    label: t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.HEADER_STYLE.SOLID'),
  },
  {
    value: 'gradient',
    label: t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.HEADER_STYLE.GRADIENT'),
  },
]);

const bubblePositionOptions = computed(() => [
  {
    value: 'left',
    label: t(
      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION.LEFT'
    ),
  },
  {
    value: 'right',
    label: t(
      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION.RIGHT'
    ),
  },
]);

const bubbleTypeOptions = computed(() => [
  {
    value: 'standard',
    label: t(
      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE.STANDARD'
    ),
  },
  {
    value: 'expanded_bubble',
    label: t(
      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE.EXPANDED_BUBBLE'
    ),
  },
]);
</script>

<template>
  <div class="flex flex-col gap-1">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.LAYOUT.LABEL')"
    >
      <SelectInput
        :model-value="appearance.layout"
        :options="[
          {
            value: 'intercom',
            label: $t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.LAYOUT.INTERCOM'),
          },
          {
            value: 'classic',
            label: $t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.LAYOUT.CLASSIC'),
          },
        ]"
        @update:model-value="updateField('layout', $event)"
      />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.HEADER_STYLE.LABEL')"
    >
      <SelectInput
        :model-value="appearance.header_style"
        :options="headerStyleSelectOptions"
        @update:model-value="updateField('header_style', $event)"
      />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.HEADER_COLOR.LABEL')"
    >
      <ColorPicker
        :model-value="appearance.header_color || widgetColor"
        @update:model-value="updateField('header_color', $event)"
      />
    </SettingsFieldSection>

    <SettingsFieldSection
      v-if="appearance.header_style === 'gradient'"
      :label="
        $t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.HEADER_GRADIENT_END.LABEL')
      "
    >
      <ColorPicker
        :model-value="appearance.header_gradient_end || widgetColor"
        @update:model-value="updateField('header_gradient_end', $event)"
      />
    </SettingsFieldSection>

    <SettingsToggleSection
      :model-value="appearance.header_fade"
      :header="$t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.HEADER_FADE.LABEL')"
      :description="
        $t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.HEADER_FADE.DESCRIPTION')
      "
      @update:model-value="updateField('header_fade', $event)"
    />

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_COLOR_LABEL')"
    >
      <ColorPicker
        :model-value="widgetColor"
        @update:model-value="$emit('update:widgetColor', $event)"
      />
    </SettingsFieldSection>

    <SettingsToggleSection
      :model-value="appearance.show_logo"
      :header="$t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.SHOW_LOGO')"
      @update:model-value="updateField('show_logo', $event)"
    />

    <SettingsFieldSection
      v-if="appearance.show_logo"
      :label="$t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.WIDGET_LOGO.LABEL')"
      :help-text="$t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.WIDGET_LOGO.HELP_TEXT')"
    >
      <div class="flex w-full flex-col gap-3">
        <InlineStorageImage
          v-if="widgetLogoUrl"
          :src="widgetLogoUrl"
          alt=""
          :height="40"
          :max-width="140"
          image-class="h-10 max-w-[140px] object-contain object-left"
          svg-wrapper-class="shrink-0"
        />
        <div class="flex items-center gap-3">
          <label
            class="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-n-brand"
          >
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml"
              class="hidden"
              @change="onWidgetLogoChange"
            />
            {{
              widgetLogoUrl
                ? $t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.WIDGET_LOGO.REPLACE')
                : $t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.WIDGET_LOGO.UPLOAD')
            }}
          </label>
          <button
            v-if="widgetLogoUrl"
            type="button"
            class="text-sm text-n-ruby-11"
            @click="emit('delete-widget-logo')"
          >
            {{ $t('INBOX_MGMT.DELETE.BUTTON_TEXT') }}
          </button>
        </div>
      </div>
    </SettingsFieldSection>

    <SettingsToggleSection
      :model-value="appearance.show_ask_card"
      :header="$t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.SHOW_ASK_CARD')"
      @update:model-value="updateField('show_ask_card', $event)"
    />

    <SettingsToggleSection
      :model-value="appearance.show_articles"
      :header="$t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.SHOW_ARTICLES')"
      @update:model-value="updateField('show_articles', $event)"
    />

    <SettingsToggleSection
      :model-value="appearance.show_agent_avatars"
      :header="$t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.SHOW_AGENT_AVATARS')"
      @update:model-value="updateField('show_agent_avatars', $event)"
    />

    <SettingsToggleSection
      :model-value="appearance.show_ai_badge"
      :header="$t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.SHOW_AI_BADGE')"
      @update:model-value="updateField('show_ai_badge', $event)"
    />

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.TABS.LABEL')"
      class="[&>div]:!items-start [&>div>label]:mt-2"
    >
      <div class="flex flex-col gap-3 w-full">
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input
            type="checkbox"
            :checked="appearance.show_tab_home"
            @change="updateField('show_tab_home', $event.target.checked)"
          />
          {{ $t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.TABS.HOME') }}
        </label>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input
            type="checkbox"
            :checked="appearance.show_tab_messages"
            @change="updateField('show_tab_messages', $event.target.checked)"
          />
          {{ $t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.TABS.MESSAGES') }}
        </label>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input
            type="checkbox"
            :checked="appearance.show_tab_help"
            @change="updateField('show_tab_help', $event.target.checked)"
          />
          {{ $t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.TABS.HELP') }}
        </label>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input
            type="checkbox"
            :checked="appearance.show_tab_news"
            @change="updateField('show_tab_news', $event.target.checked)"
          />
          {{ $t('INBOX_MGMT.WIDGET_BUILDER.APPEARANCE.TABS.NEWS') }}
        </label>
      </div>
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE')"
    >
      <div class="flex flex-col gap-4 w-full">
        <div class="flex items-center gap-2">
          <label class="text-n-slate-11 text-heading-3 shrink-0">
            {{
              $t(
                'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION_LABEL'
              )
            }}
          </label>
          <SelectInput
            :model-value="appearance.bubble_position"
            :options="bubblePositionOptions"
            class="[&>select]:!p-0 min-w-24 [&>select]:!outline-none"
            @update:model-value="updateField('bubble_position', $event)"
          />
        </div>
        <div class="flex items-center gap-2">
          <label class="text-n-slate-11 text-heading-3 shrink-0">
            {{
              $t(
                'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE_LABEL'
              )
            }}
          </label>
          <SelectInput
            :model-value="appearance.bubble_type"
            :options="bubbleTypeOptions"
            class="[&>select]:!p-0 min-w-32 [&>select]:!outline-none"
            @update:model-value="updateField('bubble_type', $event)"
          />
        </div>
        <woot-input
          :model-value="appearance.bubble_launcher_title"
          :placeholder="
            $t(
              'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_LAUNCHER_TITLE.PLACE_HOLDER'
            )
          "
          class="[&>input]:!mb-0"
          @update:model-value="updateField('bubble_launcher_title', $event)"
        />
      </div>
    </SettingsFieldSection>
  </div>
</template>
